import { CommonModule } from '@angular/common';
import { Component, inject, computed } from '@angular/core';
import {
    trigger,
    style,
    animate,
    transition,
    state,
} from '@angular/animations';
import { NgIcon } from '@ng-icons/core';
import { SnackbarService } from '../../services/snackbar.service';
import { SnackbarType, SnackbarPosition } from './snackbar.interface';

@Component({
    selector: 'app-snackbar',
    standalone: true,
    imports: [CommonModule, NgIcon],
    templateUrl: './snackbar.component.html',
    styleUrl: './snackbar.component.scss',
    animations: [
        trigger('slideAnimation', [
            // States
            state('right', style({ opacity: 1, transform: 'translateX(0)' })),
            state('left', style({ opacity: 1, transform: 'translateX(0)' })),

            // Enter from right
            transition('void => right', [
                style({ opacity: 0, transform: 'translateX(40px)' }),
                animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
            ]),
            // Leave to right
            transition('right => void', [
                animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateX(40px)' })),
            ]),

            // Enter from left
            transition('void => left', [
                style({ opacity: 0, transform: 'translateX(-40px)' }),
                animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
            ]),
            // Leave to left
            transition('left => void', [
                animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateX(-40px)' })),
            ]),
        ]),
    ],
})
export class SnackbarComponent {
    protected snackbarService = inject(SnackbarService);

    /**
     * Computed CSS classes for the snackbar
     */
    protected snackbarClasses = computed(() => {
        const state = this.snackbarService.state();
        return `snackbar snackbar--${state.type} snackbar--${state.size} snackbar--${state.position}`;
    });

    /**
     * Animation direction based on position
     */
    protected animationDirection = computed(() => {
        const position = this.snackbarService.state().position;
        return this.isRightPosition(position) ? 'right' : 'left';
    });

    private isRightPosition(position: SnackbarPosition): boolean {
        return position === 'top-right' || position === 'bottom-right';
    }

    getIconName(type: SnackbarType): string {
        switch (type) {
            case 'error':
                return 'WarningCircle';
            case 'warning':
                return 'WarningTriangle';
            case 'success':
                return 'CheckCircle';
            default:
                return '';
        }
    }

    hasIcon(type: SnackbarType): boolean {
        return type === 'error' || type === 'warning' || type === 'success';
    }

    dismiss(): void {
        this.snackbarService.hide();
    }
}
